import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "../../lib/api";
import { SALayout } from "../../components/superadmin/SALayout";
import { formatDate } from "../../lib/format";
import { useSAAuth } from "../../contexts/SAAuthContext";
import { Plus, Trash2, X, Copy } from "lucide-react";

const ROLES = ["Admin", "Viewer"];

export default function SATeamManagement() {
  const { user } = useSAAuth();
  const isAdmin = user?.role === "Admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/team");
    setRows(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const changeRole = async (row, role) => {
    try {
      await api.put(`/admin/team/${row.id}`, { role });
      toast.success("Role updated");
      await load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove ${row.email} from the team?`)) return;
    try {
      await api.delete(`/admin/team/${row.id}`);
      toast.success("Removed");
      await load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <SALayout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight">Team Management</h1>
          <p className="text-sm text-[#A0A0A0] mt-1">Founding team & internal admins with access to TabOn Superadmin.</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowAdd(true)}
            data-testid="sa-team-invite"
            className="flex items-center gap-1.5 bg-[#9D00FF] hover:bg-[#8C00E6] text-white text-sm font-semibold rounded-lg px-4 py-2"
          >
            <Plus size={14} /> Invite User
          </button>
        )}
      </div>

      <div className="bg-[#1A1A1A] border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="sa-team-table">
            <thead className="bg-[#0F0F0F] text-[#A0A0A0] text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Role</th>
                <th className="px-4 py-3 text-left">Last Login</th>
                <th className="px-4 py-3 text-left">Added</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {loading && <tr><td colSpan={6} className="px-4 py-6 text-center text-[#A0A0A0]">Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-[#A0A0A0]">No team members.</td></tr>
              )}
              {rows.map((r) => {
                const isSelf = r.id === user?.id;
                return (
                  <tr key={r.id} className="hover:bg-white/5" data-testid={`sa-team-row-${r.id}`}>
                    <td className="px-4 py-3 font-semibold text-white">
                      {r.name}{isSelf && <span className="ml-2 text-[10px] text-[#9D00FF] uppercase tracking-wider">you</span>}
                    </td>
                    <td className="px-4 py-3 text-white/90">{r.email}</td>
                    <td className="px-4 py-3">
                      {isAdmin && !isSelf ? (
                        <select
                          value={r.role}
                          onChange={(e) => changeRole(r, e.target.value)}
                          data-testid={`sa-team-role-${r.id}`}
                          className="bg-[#0A0A0A] border border-white/10 rounded-lg px-2 py-1 text-xs text-white"
                        >
                          {ROLES.map((ro) => <option key={ro}>{ro}</option>)}
                        </select>
                      ) : (
                        <span className="text-xs font-bold uppercase tracking-wider bg-[#9D00FF]/20 border border-[#9D00FF]/40 rounded-full px-2 py-0.5 text-[#D78CFF]">{r.role}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-white/80">{r.last_login ? formatDate(r.last_login) : "Never"}</td>
                    <td className="px-4 py-3 text-white/80">{formatDate(r.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      {isAdmin && !isSelf ? (
                        <button
                          onClick={() => remove(r)}
                          data-testid={`sa-team-remove-${r.id}`}
                          className="text-[#EF4444] hover:text-red-400 inline-flex items-center gap-1 text-xs font-semibold"
                        >
                          <Trash2 size={13} /> Remove
                        </button>
                      ) : <span className="text-[#A0A0A0] text-xs">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showAdd && (
        <InviteDialog
          onClose={() => setShowAdd(false)}
          onCreated={async (info) => { setShowAdd(false); setInviteInfo(info); await load(); }}
        />
      )}
      {inviteInfo && <InviteResult info={inviteInfo} onClose={() => setInviteInfo(null)} />}
    </SALayout>
  );
}

function InviteDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", role: "Viewer" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/team", form);
      toast.success("Invite created");
      onCreated(data);
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">Invite Team Member</h3>
          <button type="button" onClick={onClose} className="p-2 border border-white/10 rounded-lg"><X size={14} /></button>
        </div>
        <label className="block mb-3">
          <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1">Name</div>
          <input
            required data-testid="sa-invite-name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
          />
        </label>
        <label className="block mb-3">
          <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1">Email</div>
          <input
            required type="email" data-testid="sa-invite-email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
          />
        </label>
        <label className="block mb-4">
          <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1">Role</div>
          <select
            data-testid="sa-invite-role" value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
          >
            {ROLES.map((r) => <option key={r}>{r}</option>)}
          </select>
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="border border-white/10 rounded-lg px-4 py-2 text-sm">Cancel</button>
          <button
            type="submit" disabled={loading}
            data-testid="sa-invite-submit"
            className="bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-60 text-white text-sm font-semibold rounded-lg px-5 py-2"
          >
            {loading ? "Sending…" : "Create Invite"}
          </button>
        </div>
      </form>
    </div>
  );
}

function InviteResult({ info, onClose }) {
  const link = `${window.location.origin}/admin/invite/${info.invite_token}`;
  const copy = () => {
    navigator.clipboard.writeText(link);
    toast.success("Invite link copied");
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold mb-1">Invite Created</h3>
        <p className="text-sm text-[#A0A0A0] mb-4">Share this one-time link with <span className="text-white">{info.email}</span>. Valid for 48 hours.</p>
        <div className="flex gap-2">
          <input
            readOnly value={link} data-testid="sa-invite-link"
            className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-xs text-white font-mono"
          />
          <button
            onClick={copy} data-testid="sa-invite-copy"
            className="flex items-center gap-1 bg-[#9D00FF] hover:bg-[#8C00E6] text-white text-xs font-semibold rounded-lg px-3 py-2"
          >
            <Copy size={13} /> Copy
          </button>
        </div>
        <div className="flex justify-end mt-5">
          <button onClick={onClose} className="border border-white/10 rounded-lg px-4 py-2 text-sm">Done</button>
        </div>
      </div>
    </div>
  );
}
